import React, { Fragment, useEffect, useRef, useState } from "react";
import styles from "./NavBar.module.scss";
import SparkleIcon from "@src/assets/website-logo.svg";
import SparkleIconLight from "@src/assets/website-logo-light.svg";
import Sparklewhite from "@src/assets/sparklewhite.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearTokens } from "@src/app/authSlice";
import {
  Category,
  clearUserDetails,
  setCategoryDropdownData,
  setGlobalState,
  setSelectedChallengeCategory,
  setIntendedPath,
  setUserLogin,
  clearIntendedPath,
  setTheme,
  toggleWebsiteTheme,
} from "@src/app/globalSlice";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { persistor, RootState } from "@src/app/store";
import { clearHeaderState } from "@src/app/headersSlice";
import GradiantButton from "../gradiant-btn/GradiantButton";
import { Tooltip, Typography } from "top-brains-ui";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { COMMON_COLORS, WEBSITE_COLORS } from "@src/styles/ColorConstants";
import { ChevronDown, ChevronUp, Menu, X, Sun, Moon } from "lucide-react";
import { truncateText } from "@src/utils/CommonUtils";
import userProfile from "@src/assets/website_user.svg";
import Logout from "@src/assets/website-log-out.svg";
import { useLazyGetCategoryQuery } from "@src/api/CategoryApiSlice";

export interface SubmenuItem {
  label: string;
  onClick: (link: string) => void;
  icon?: React.ReactNode | string;
}

export interface LinkItem {
  label: string;
  onClick?: () => void;
  authRequired?: boolean;
  authRedirect?: string;
  submenu?: SubmenuItem[];
}

interface NavbarProps {
  links?: LinkItem[];
  showLinks?: boolean;
  showLogo?: boolean;
}

const NavBar: React.FC<NavbarProps> = ({
  links = [],
  showLinks = true,
  showLogo = true,
}) => {
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated = !!accessToken;
  const dispatch = useDispatch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [challengeMenuOpen, setChallengeMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [clickedLink, setClickedLink] = useState<string | null>(null);
  const isLogin = useSelector((state: RootState) => state.globalSlice.isLogin);
  const { userDetails, challengeCategory, selectedChallengeCategory, theme } =
    useSelector((state: RootState) => state.globalSlice);
  const hasMultipleRoles = (userDetails?.privileges?.length || 0) > 1;
  const location = useLocation();

  const isOutsidePage =
    location.pathname === "/" ||
    location.pathname === "/home" ||
    location.pathname.startsWith("/challenges") ||
    location.pathname.startsWith("/website-quiz") ||
    location.pathname.startsWith("/website-tournaments") ||
    location.pathname.startsWith("/student-organization");

  const handleToggleTheme = () => {
    dispatch(toggleWebsiteTheme());
  };

  const navRef = useRef<HTMLDivElement>(null);
  const activeMenuRef = useRef<string | null>(null);

  const [getCategories, { data: categories, isFetching }] =
    useLazyGetCategoryQuery();

  useEffect(() => {
    getCategories();
  }, [userDetails?.id]);

  useEffect(() => {
    if (categories?.response?.length) {
      dispatch(setCategoryDropdownData(categories.response));
    }
    if (!selectedChallengeCategory?.id) {
      const programmingCartegory = categories?.response?.find(
        (item) => item.name?.toLowerCase() === "programming",
      );
      dispatch(setSelectedChallengeCategory(programmingCartegory));
    }
  }, [categories, dispatch, selectedChallengeCategory?.id]);

  useEffect(() => {
    dispatch(setGlobalState({ isChallengeCategoryFetching: isFetching }));
  }, [isFetching, dispatch]);

  useEffect(() => {
    activeMenuRef.current = activeMenu;
  }, [activeMenu]);

  useEffect(() => {
    if (!isLogin) {
      const pathToLabel: Record<string, string> = {
        "/challenges": "Challenges",
        "/website-quiz": "Quiz",
        "/website-tournaments": "Tournaments",
        "/student-organization": "My Org's",
        "/quiz": "My Org's",
        "/organizations": "My Org's",
        "/subjects": "My Org's",
      };

      const matchedLabel = Object.entries(pathToLabel).find(([path]) =>
        location.pathname.startsWith(path),
      )?.[1];

      setClickedLink(matchedLabel || null);
      setSubmenuOpen(false);
      setActiveMenu(null);
    }
  }, [isLogin, location.pathname]);

  const handleLogout = () => {
    dispatch(clearUserDetails());
    dispatch(clearHeaderState());
    dispatch(clearTokens());
    persistor.purge();
    setClickedLink("");
    navigate("/");
  };

  const handleSwitchRoleNavigation = () => {
    dispatch(clearIntendedPath());

    const privileges = userDetails?.privileges || [];
    if (privileges.length > 1) {
      if (privileges.includes(USER_CONSTANTS.STUDENT)) {
        navigate("/choose-role");
      } else {
        navigate("/multi_roles");
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (navRef.current && !navRef.current.contains(event.target as Node)) {
      setSubmenuOpen(false);
      setActiveMenu(null);
      setChallengeMenuOpen(false);

      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }

      if (activeMenuRef.current === "Challenges") {
        setClickedLink(null);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileMenuOpen, challengeMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileMenuOpen]);
  const handleChallengesClick = (categoryObject: Category) => {
    setChallengeMenuOpen(false);
    dispatch(setSelectedChallengeCategory(categoryObject));
    navigate(`/challenges/Programming`);
  };

  return (
    <>
      <div className={styles.navbar} ref={navRef}>
        {showLogo && (
          <div
            className={styles.logo}
            onClick={() => {
              setClickedLink(null);
              dispatch(clearIntendedPath());
              navigate("/home");
            }}
          >
            <img
              src={
                theme === "website-light-theme" ? SparkleIconLight : SparkleIcon
              }
              alt="Sparkle Icon"
              className={styles.icon}
            />
            <div className={styles.webName}>
              <Typography fontWeight="bold" fontSize={"24px"}>
                Top
              </Typography>
              <Typography
                fontWeight="bold"
                fontSize={"24px"}
                color={WEBSITE_COLORS.DARK_GREEN}
              >
                Brains
              </Typography>
            </div>
          </div>
        )}

        <div
          className={styles.mobileMenuIcon}
          onClick={(e) => {
            e.stopPropagation();
            setMobileMenuOpen((prev) => !prev);
          }}
        >
          {mobileMenuOpen ? (
            <X size={24} color="#FAFAFA" />
          ) : (
            <Menu size={24} color="#FAFAFA" />
          )}
        </div>

        <div className={styles.navbarRightSection}>
          {" "}
          {showLinks && (
            <div className={styles.links}>
              {links.map((link, index) => (
                <span
                  className={`${styles.link} ${
                    clickedLink === link.label ? styles.clicked : ""
                  }`}
                  key={`navbar-link-${link.label}-${index}`}
                  onClick={() => {
                    if (link.submenu) {
                      if (activeMenu === link.label) {
                        setActiveMenu(null);
                      } else {
                        setActiveMenu(link.label);
                        setSubmenuOpen(true);
                      }
                    } else if (link.onClick) {
                      link.onClick();
                    }

                    if (link.label.toLowerCase() !== "challenges") {
                      setSubmenuOpen(false);
                      setActiveMenu(null);
                    } else if (link.label.toLowerCase() == "challenges") {
                      // setSubmenuOpen(true);
                    }
                  }}
                  onMouseEnter={() => {
                    if (link.label?.toLowerCase() === "challenges") {
                      setChallengeMenuOpen(true);
                    }
                  }}
                >
                  {link.label}
                  {link.submenu && activeMenu === link.label && submenuOpen && (
                    <div className={styles.submenu}>
                      <div className={styles.submenuContent}>
                        {link.submenu.map((submenuItem, subIndex) => (
                          <Fragment key={`submenu-item-${subIndex}`}>
                            <Tooltip
                              title={
                                submenuItem.label.length > 22
                                  ? submenuItem.label
                                  : ""
                              }
                            >
                              <div
                                className={styles.submenuItem}
                                onClick={() => {
                                  submenuItem?.onClick(
                                    submenuItem.label.toLowerCase(),
                                  );
                                  setSubmenuOpen(false);
                                  setActiveMenu(null);
                                }}
                              >
                                <img
                                  alt="submenu icon"
                                  src={`data:image/png;base64,${submenuItem.icon}`}
                                  className={styles.submenuIcon}
                                />
                                <span>{submenuItem.label}</span>
                              </div>
                            </Tooltip>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                  {link.label.toLowerCase() === "challenges" ? (
                    challengeMenuOpen ? (
                      <ChevronUp
                        width={16}
                        height={20}
                        strokeWidth={3}
                        color="var(--website-text-color4)"
                      />
                    ) : (
                      <ChevronDown
                        width={16}
                        height={20}
                        strokeWidth={3}
                        color="var(--website-text-color4)"
                      />
                    )
                  ) : null}
                </span>
              ))}
            </div>
          )}
          {isOutsidePage && (
            <button
              onClick={handleToggleTheme}
              className={styles.themeToggleBtn}
              title={
                theme === "website-theme"
                  ? "Switch to Light Mode"
                  : "Switch to Dark Mode"
              }
              aria-label="Toggle Theme"
            >
              {theme === "website-theme" ? (
                <Moon size={18} className={styles.themeIcon} />
              ) : (
                <Sun size={18} className={styles.themeIcon} />
              )}
            </button>
          )}
          <div className={styles.signIn}>
            {!isAuthenticated ? (
              <GradiantButton
                variant="tertiary"
                label="Sign In"
                img={Sparklewhite}
                height="36px"
                width="100px"
                className={styles.navbarSignInButton}
                onClick={() => dispatch(setUserLogin(true))}
                labelClassName={styles.navbarSignInLabel}
              />
            ) : (
              <div
                className={styles.avatarWrapper}
                onClick={() => setSubmenuOpen((prev) => !prev)}
              >
                <div className={styles.avatar}>
                  {userDetails?.name?.charAt(0)?.toUpperCase()}
                </div>
                <ChevronDown
                  width={14}
                  height={16}
                  strokeWidth={2}
                  color="var(--website-text-color4)"
                />
                {submenuOpen && (
                  <div
                    style={{
                      width: "240px",
                      border: "1px solid var(--border-color, #222222)",
                      position: "absolute",
                      top: "44px",
                      right: "0px",
                      backgroundColor: "var(--common-bg-color4, #000000)",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        height: "49px",
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        borderBottom: "1px solid var(--border-color, #222222)",
                        padding: "4px 16px",
                      }}
                    >
                      <div
                        style={{
                          height: "40px",
                          width: "40px",
                          borderRadius: "50%",
                          backgroundColor: "var(--website-text-green-color)",
                          color: "var(--website-text-color1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {userDetails?.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>{truncateText(userDetails?.name, 13)}</div>
                    </div>
                    {userDetails?.currentPrivilege?.toLowerCase() ===
                      "student" && (
                      <div
                        onClick={() => navigate("/profile")}
                        style={{
                          display: "flex",
                          gap: "8px",
                          padding: "8px",
                          alignItems: "center",
                          height: "36px",
                        }}
                      >
                        <img src={userProfile} height={16} width={16} />
                        <Typography
                          fontSize={14}
                          color="var(--website-text-color1, #FAFAFA)"
                        >
                          View Profile
                        </Typography>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        padding: "8px",
                        alignItems: "center",
                        height: "36px",
                      }}
                      onClick={() => navigate("/change-password")}
                    >
                      <img src={userProfile} height={16} width={16} />
                      <Typography
                        fontSize={14}
                        color="var(--website-text-color1, #FAFAFA)"
                      >
                        Change Password
                      </Typography>
                    </div>
                    {hasMultipleRoles && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          padding: "8px",
                          alignItems: "center",
                          height: "36px",
                        }}
                        onClick={handleSwitchRoleNavigation}
                      >
                        <img src={userProfile} height={16} width={16} />
                        <Typography
                          fontSize={14}
                          color="var(--website-text-color1, #FAFAFA)"
                        >
                          Switch Role
                        </Typography>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        padding: "8px",
                        alignItems: "center",
                        height: "36px",
                      }}
                      onClick={handleLogout}
                    >
                      <img src={Logout} height={16} width={16} />
                      <Typography
                        fontSize={14}
                        color="var(--website-text-color1, #FAFAFA)"
                      >
                        Log out
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {challengeMenuOpen ? (
            <div
              className={styles.challengeMenu}
              onMouseEnter={() => setChallengeMenuOpen(true)}
              onMouseLeave={() => setChallengeMenuOpen(false)}
            >
              {challengeCategory?.map((category: Category) => (
                <span
                  key={category?.id}
                  className={styles.category}
                  style={{
                    color:
                      (location.pathname?.includes("challenges") ||
                        location.pathname === "/") &&
                      selectedChallengeCategory?.id === category.id
                        ? COMMON_COLORS.SUCCESS_GREEN
                        : "",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChallengesClick(category);
                  }}
                >
                  {truncateText(category?.name, 28)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {mobileMenuOpen && (
          <div className={styles.mobileMenuContainer}>
            <div className={styles.mobileLinks}>
              {links.map((link, index) => (
                <div
                  key={`mobile-link-${index}`}
                  className={styles.mobileLink}
                  onClick={() => {
                    if (link.onClick) link.onClick();
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </div>
              ))}
              {isOutsidePage && (
                <div
                  className={styles.mobileLink}
                  onClick={() => {
                    handleToggleTheme();
                    setMobileMenuOpen(false);
                  }}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {theme === "website-theme" ? (
                    <>
                      <Moon size={18} />
                      <span>Dark Mode</span>
                    </>
                  ) : (
                    <>
                      <Sun size={18} />
                      <span>Light Mode</span>
                    </>
                  )}
                </div>
              )}

              {isAuthenticated ? (
                <>
                  {userDetails?.currentPrivilege === "student" && (
                    <div
                      className={styles.mobileLink}
                      onClick={() => {
                        navigate("/profile");
                        setMobileMenuOpen(false);
                      }}
                    >
                      View Profile
                    </div>
                  )}
                  <div
                    className={styles.mobileLink}
                    onClick={() => navigate("/change-password")}
                  >
                    Change Password
                  </div>
                  {hasMultipleRoles && (
                    <div
                      className={styles.mobileLink}
                      onClick={handleSwitchRoleNavigation}
                    >
                      Switch Role
                    </div>
                  )}
                  <div
                    className={styles.mobileLink}
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Logout
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={styles.mobileLink}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      dispatch(setUserLogin(true));
                    }}
                  >
                    Sign In
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NavBar;
